import React, { useState, useRef, useEffect } from 'react';
import { V } from '../utils/theme';
import { Icons } from './Icons';

// ─────────────────────────────────────────────────────────────────────────────
// Shared design-kit pieces introduced with the landing / onboarding redesign.
// Pure presentational, theme-token driven (dark + light), no dependency on
// components/ui so it can be imported from anywhere without cycles.
// ─────────────────────────────────────────────────────────────────────────────

export const gradient = () => `linear-gradient(135deg,${V.accent},${V.accent2})`;
export const track = () => (V.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.08)");
// Foreground colour for text/icons placed on the accent gradient.
// V.onAccent is owned by utils/brand.js (brand `on_accent`, else bg/white by mode).
export const onAccent = () => V.onAccent || (V.mode === "dark" ? V.bg : "#fff");

// Brand mark — gradient tile with the dumbbell glyph, or the org's logo when branded
export const LogoMark = ({ size = 64, radius, style = {} }) => {
  const logo = V.brand && (V.brand.logo_url || V.brand.logo_small_data);
  const r = radius ?? Math.round(size * 0.3);
  return (
    <div aria-hidden="true" style={{
      width: size, height: size, borderRadius: r, background: gradient(),
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      boxShadow: `0 ${Math.round(size / 4)}px ${Math.round(size * 0.75)}px -${Math.round(size / 5)}px ${V.accent}66`,
      ...(logo ? { overflow: "hidden" } : {}),
      ...style,
    }}>
      {logo
        ? <img src={logo} alt="" draggable={false} style={{ width: size, height: size, objectFit: "cover", borderRadius: r, display: "block" }} />
        : Icons.dumbbell({ size: Math.round(size * 0.52), color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 2.4 })}
    </div>
  );
};

// "IRON" + gradient "LOG" — or the org's app name (plain) when branded
export const Wordmark = ({ size = 28, style = {} }) => {
  const name = V.brand && V.brand.app_name;
  if (name) return <span style={{ fontSize: size, fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1, color: V.text, whiteSpace: "nowrap", ...style }}>{name}</span>;
  return (
    <span style={{ fontSize: size, fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1, color: V.text, ...style }}>
      IRON<span style={{
        background: `linear-gradient(90deg,${V.accent},${V.accent2})`,
        WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent",
      }}>LOG</span>
    </span>
  );
};

// Small uppercase label used above sections and inputs
export const Eyebrow = ({ children, color, style = {} }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase",
    color: color || V.text3, ...style }}>{children}</div>
);

// Icon tile used in step headers and feature tiles
export const IconTile = ({ icon, color = V.accent, size = 44, iconSize }) => (
  <div aria-hidden="true" style={{
    width: size, height: size, borderRadius: Math.round(size * 0.3), flexShrink: 0,
    background: `linear-gradient(135deg,${color}26,${color}0a)`, border: `1px solid ${color}30`,
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    {icon({ size: iconSize || Math.round(size * 0.45), color, strokeWidth: 2.2 })}
  </div>
);

// Segmented step progress — one pill per step, current one glows
export const ProgressSteps = ({ step, labels }) => {
  const total = labels.length;
  return (
    <div aria-label={`Step ${step} of ${total}: ${labels[step - 1]}`} role="progressbar"
      aria-valuemin={1} aria-valuemax={total} aria-valuenow={step}>
      <div style={{ display: "flex", gap: 5 }}>
        {labels.map((l, i) => {
          const n = i + 1;
          const done = n < step, cur = n === step;
          return (
            <div key={l} style={{ flex: 1, height: 5, borderRadius: 3, overflow: "hidden", background: track() }}>
              <div style={{
                height: "100%", width: done || cur ? "100%" : "0%", borderRadius: 3,
                background: done ? V.accent : gradient(),
                boxShadow: cur ? `0 0 10px ${V.accent}80` : "none",
                opacity: done ? 0.55 : 1,
                transition: "width .35s cubic-bezier(.2,.7,.3,1)",
              }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: V.text }}>{labels[step - 1]}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: V.text3, fontFamily: V.mono }}>{step} / {total}</span>
      </div>
    </div>
  );
};

// autofill, keychain save, one-time-code suggestions and E2E fills all work.
export const CodeInput = ({ value, onChange, length = 6, secret, label, autoFocus, autoComplete, onEnter, state, ariaLabel, name }) => {
  const ref = useRef(null);
  const [focus, setFocus] = useState(false);
  const chars = value.split("");
  const activeIdx = Math.min(value.length, length - 1);
  const ringColor = state === "error" ? V.danger : state === "ok" ? V.accent : V.accent;
  useEffect(() => { if (autoFocus) { const t = setTimeout(() => ref.current?.focus(), 60); return () => clearTimeout(t); } }, [autoFocus]);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <Eyebrow color={focus ? V.accent : V.text3} style={{ marginBottom: 8, textAlign: "center", transition: "color .15s" }}>{label}</Eyebrow>}
      <div style={{ position: "relative" }} onClick={() => ref.current?.focus()}>
        <div aria-hidden="true" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          {Array.from({ length }).map((_, i) => {
            const filled = i < chars.length;
            const active = focus && i === activeIdx && value.length < length;
            const border = state === "error" ? V.danger : (filled || active) ? ringColor : V.cardBorder;
            return (
              <div key={i} style={{
                flex: "0 1 48px", height: 56, borderRadius: 12, background: V.inputBg,
                border: `1.5px solid ${border}`, boxShadow: active ? `0 0 0 3px ${ringColor}22` : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: V.mono, fontSize: secret ? 22 : 24, fontWeight: 700, color: state === "error" ? V.danger : V.text,
                transition: "border-color .12s, box-shadow .12s",
              }}>
                {filled ? (secret ? "●" : chars[i]) : active ? <span style={{ width: 2, height: 22, background: V.accent, borderRadius: 1, animation: "pulse 1s ease-in-out infinite" }} /> : ""}
              </div>
            );
          })}
        </div>
        <input ref={ref} type={secret ? "password" : "text"} inputMode="numeric" pattern="[0-9]*" name={name}
          value={value} maxLength={length} autoComplete={autoComplete} aria-label={ariaLabel || label || "Code"}
          onChange={e => onChange(e.target.value.replace(/\D/g, "").slice(0, length))}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          onKeyDown={e => { if (e.key === "Enter" && onEnter && value.length === length) onEnter(); }}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, border: "none",
            background: "transparent", color: "transparent", caretColor: "transparent", fontSize: 16, cursor: "text",
          }} />
      </div>
    </div>
  );
};

// Inline status line under inputs — error / success / neutral
// Card / section heading: small icon tile + bold label, optional right-side slot.
// Replaces the emoji-prefixed headings ("📅 Today") used across the social tabs.
export const SectionTitle = ({ icon, color = V.accent, children, right, size = 13, style = {} }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, ...style }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      {icon && <IconTile icon={icon} color={color} size={26} iconSize={14} />}
      <span style={{ fontSize: size, fontWeight: 700, color: V.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
    </div>
    {right}
  </div>
);

export const Notice = ({ tone = "neutral", children, center, style = {} }) => {
  if (!children) return null;
  const color = tone === "error" ? V.danger : tone === "ok" ? V.accent : tone === "warn" ? V.warn : V.text3;
  return (
    <div role={tone === "error" ? "alert" : undefined} style={{
      fontSize: 12, color, lineHeight: 1.5, textAlign: center ? "center" : "left",
      display: "flex", alignItems: "center", gap: 6, justifyContent: center ? "center" : "flex-start", ...style,
    }}>
      {tone === "ok" && Icons.check({ size: 14, color, strokeWidth: 3 })}
      <span>{children}</span>
    </div>
  );
};

// Muted info callout
export const Callout = ({ icon, color = V.accent, title, children, style = {} }) => (
  <div style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 14, background: `${color}0d`, border: `1px solid ${color}22`, ...style }}>
    {icon && <div style={{ flexShrink: 0, marginTop: 1 }}>{icon({ size: 16, color })}</div>}
    <div style={{ fontSize: 12, color: V.text2, lineHeight: 1.55 }}>
      {title && <div style={{ fontWeight: 700, color: V.text, marginBottom: 2 }}>{title}</div>}
      {children}
    </div>
  </div>
);

// Empty state with a reason and a way forward
export const EmptyState = ({ icon, color = V.accent, title, children, action, onAction, style = {} }) => (
  <div style={{ padding: "22px 16px", borderRadius: 16, textAlign: "center", background: `${color}08`, border: `1px dashed ${color}33`, ...style }}>
    {icon && <div style={{ display: "inline-flex", marginBottom: 8 }}><IconTile icon={icon} color={color} size={40} iconSize={20} /></div>}
    {title && <div style={{ fontSize: 14, fontWeight: 700, color: V.text }}>{title}</div>}
    {children && <div style={{ fontSize: 12, color: V.text3, marginTop: 4, lineHeight: 1.5 }}>{children}</div>}
    {action && onAction && (
      <button type="button" onClick={onAction} style={{ marginTop: 12, padding: "9px 16px", borderRadius: 10, border: "none", cursor: "pointer",
        background: color, color: onAccent(), fontSize: 12, fontWeight: 700, fontFamily: V.font }}>{action}</button>
    )}
  </div>
);

// Text-style button
export const LinkBtn = ({ onClick, children, color, disabled, style = {} }) => (
  <button type="button" onClick={onClick} disabled={disabled} style={{
    background: "none", border: "none", padding: "6px 4px", cursor: disabled ? "default" : "pointer", fontFamily: V.font,
    fontSize: 13, fontWeight: 600, color: disabled ? V.text3 : (color || V.accent), WebkitTapHighlightColor: "transparent", ...style,
  }}>{children}</button>
);

// Numbered how-to step (install guide, update guide)
// ─── Install guide modal (portalled so position:fixed survives iOS scroll containers) ───
export const NumberedStep = ({ n, title, desc, chip }) => (
  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
    <div style={{ width: 30, height: 30, borderRadius: 10, background: `${V.accent}14`, border: `1px solid ${V.accent}28`,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: V.accent, fontFamily: V.mono }}>{n}</span>
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>{title}</div>
      {desc && <div style={{ fontSize: 11, color: V.text3, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>}
      {chip && <div style={{ marginTop: 6, padding: "6px 10px", background: V.inputBg, borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${V.cardBorder}` }}>
        {chip.icon}<span style={{ fontSize: 11, color: V.text2, fontWeight: 600 }}>{chip.label}</span>
      </div>}
    </div>
  </div>
);
